import React, { useState, useEffect } from 'react';
import { FaDownload, FaFileAlt, FaChartBar, FaEye, FaCalendarAlt, FaTicketAlt, FaDollarSign, FaSpinner, FaVideo, FaUsers, FaTv } from 'react-icons/fa';
import { 
  getComparativaComediantes, 
  descargarInformeComediante, 
  descargarInformeComparativo,
  descargarInformeVideos,
  descargarInformeEventos,
  descargarInformeGeneral,
  getEstadisticasGenerales
} from '../../../components/http/Actions/EstadisticasAction';

const Reportes = () => {
    const [comediantes, setComediantes] = useState([]);
    const [estadisticasGenerales, setEstadisticasGenerales] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingStates, setDownloadingStates] = useState({});
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            await Promise.all([
                getComparativaComediantes(setComediantes, setError, token),
                getEstadisticasGenerales(setEstadisticasGenerales, setError, token)
            ]);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Funciones de descarga existentes...
    const handleDescargarInforme = async (comedianteId, nombreComediante, token) => {
        try {
            setDownloadingStates(prev => ({ ...prev, [comedianteId]: true }));
            
            const fechaInicioDate = fechaInicio ? new Date(fechaInicio) : null;
            const fechaFinDate = fechaFin ? new Date(fechaFin) : null;
            const token = localStorage.getItem('token');
            await descargarInformeComediante(comedianteId, fechaInicioDate, fechaFinDate, token);
        } catch (error) {
            alert(`Error al descargar el informe: ${error}`);
        } finally {
            setDownloadingStates(prev => ({ ...prev, [comedianteId]: false }));
        }
    };

    // NUEVAS: Funciones de descarga específicas
    const handleDescargarInformeVideos = async (comedianteId, nombreComediante) => {
        try {
            setDownloadingStates(prev => ({ ...prev, [`videos_${comedianteId}`]: true }));
            
            const fechaInicioDate = fechaInicio ? new Date(fechaInicio) : null;
            const fechaFinDate = fechaFin ? new Date(fechaFin) : null;
            const token = localStorage.getItem('token');
            await descargarInformeVideos(comedianteId, fechaInicioDate, fechaFinDate, token);
        } catch (error) {
            alert(`Error al descargar el informe de videos: ${error}`);
        } finally {
            setDownloadingStates(prev => ({ ...prev, [`videos_${comedianteId}`]: false }));
        }
    };

    const handleDescargarInformeEventos = async (comedianteId, nombreComediante) => {
        try {
            setDownloadingStates(prev => ({ ...prev, [`eventos_${comedianteId}`]: true }));
            
            const fechaInicioDate = fechaInicio ? new Date(fechaInicio) : null;
            const fechaFinDate = fechaFin ? new Date(fechaFin) : null;
            const token = localStorage.getItem('token');
            await descargarInformeEventos(comedianteId, fechaInicioDate, fechaFinDate, token);
        } catch (error) {
            alert(`Error al descargar el informe de eventos: ${error}`);
        } finally {
            setDownloadingStates(prev => ({ ...prev, [`eventos_${comedianteId}`]: false }));
        }
    };

    const handleDescargarComparativo = async () => {
        try {
            setDownloadingStates(prev => ({ ...prev, 'comparativo': true }));
            const token = localStorage.getItem('token');
            await descargarInformeComparativo(token);
        } catch (error) {
            alert(`Error al descargar el informe comparativo: ${error}`);
        } finally {
            setDownloadingStates(prev => ({ ...prev, 'comparativo': false }));
        }
    };

    const handleDescargarGeneral = async () => {
        try {
            setDownloadingStates(prev => ({ ...prev, 'general': true }));
            const token = localStorage.getItem('token');
            await descargarInformeGeneral(token);
        } catch (error) {
            alert(`Error al descargar el informe general: ${error}`);
        } finally {
            setDownloadingStates(prev => ({ ...prev, 'general': false }));
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP'
        }).format(amount);
    };

    const formatNumber = (number) => {
        return new Intl.NumberFormat('es-CL').format(number);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
                <div className="text-center">
                    <FaSpinner className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-4" />
                    <p className="text-white text-lg">Cargando datos de reportes...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-900">
            <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-white mb-2">Módulo de Reportes</h1>
                    <p className="text-gray-400">Genera y descarga informes detallados de performance</p>
                </div>

                {/* Estadísticas generales */}
                {estadisticasGenerales && (
                    <div className="bg-zinc-800 rounded-xl shadow-lg p-6 mb-8">
                        <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                            <FaChartBar className="mr-2" />
                            Resumen de la Plataforma
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-zinc-700 rounded-lg p-4 text-center">
                                <div className="text-2xl font-bold text-blue-400">{formatNumber(estadisticasGenerales.totalUsuarios)}</div>
                                <div className="text-sm text-gray-400">Usuarios Totales</div>
                            </div>
                            <div className="bg-zinc-700 rounded-lg p-4 text-center">
                                <div className="text-2xl font-bold text-green-400">{formatNumber(estadisticasGenerales.totalVideos)}</div>
                                <div className="text-sm text-gray-400">Videos Totales</div>
                            </div>
                            <div className="bg-zinc-700 rounded-lg p-4 text-center">
                                <div className="text-2xl font-bold text-purple-400">{formatNumber(estadisticasGenerales.totalEventos)}</div>
                                <div className="text-sm text-gray-400">Eventos Totales</div>
                            </div>
                            <div className="bg-zinc-700 rounded-lg p-4 text-center">
                                <div className="text-2xl font-bold text-yellow-400">{formatCurrency(estadisticasGenerales.ingresosTotales)}</div>
                                <div className="text-sm text-gray-400">Ingresos Totales</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filtros de fecha */}
                <div className="bg-zinc-800 rounded-xl shadow-lg p-6 mb-8">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                        <FaCalendarAlt className="mr-2" />
                        Filtros de Fecha (Opcional)
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-400 text-sm font-medium mb-2">
                                Fecha de Inicio
                            </label>
                            <input
                                type="date"
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.target.value)}
                                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-400 text-sm font-medium mb-2">
                                Fecha de Fin
                            </label>
                            <input
                                type="date"
                                value={fechaFin}
                                onChange={(e) => setFechaFin(e.target.value)}
                                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Informes globales */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* Informe General */}
                    <div className="bg-gradient-to-r from-blue-500 to-blue-700 rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-white mb-2">Informe General</h2>
                                <p className="text-blue-100">Análisis completo de toda la plataforma con métricas clave</p>
                            </div>
                            <button
                                onClick={handleDescargarGeneral}
                                disabled={downloadingStates['general']}
                                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {downloadingStates['general'] ? (
                                    <>
                                        <FaSpinner className="w-5 h-5 mr-2 animate-spin" />
                                        Generando...
                                    </>
                                ) : (
                                    <>
                                        <FaTv className="w-5 h-5 mr-2" />
                                        Descargar PDF
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Informe Comparativo */}
                    <div className="bg-gradient-to-r from-orange-500 to-orange-700 rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-white mb-2">Informe Comparativo</h2>
                                <p className="text-orange-100">Comparación detallada entre todos los comediantes</p>
                            </div>
                            <button
                                onClick={handleDescargarComparativo}
                                disabled={downloadingStates['comparativo']}
                                className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {downloadingStates['comparativo'] ? (
                                    <>
                                        <FaSpinner className="w-5 h-5 mr-2 animate-spin" />
                                        Generando...
                                    </>
                                ) : (
                                    <>
                                        <FaChartBar className="w-5 h-5 mr-2" />
                                        Descargar PDF
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Lista de comediantes */}
                {error ? (
                    <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6">
                        <p>{error}</p>
                    </div>
                ) : (
                    <div className="bg-zinc-800 rounded-xl shadow-lg p-6">
                        <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                            <FaFileAlt className="mr-2" />
                            Informes por Comediante
                        </h2>
                        
                        {comediantes.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-gray-400">No hay comediantes disponibles para generar reportes</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-6">
                                {comediantes.map((comediante) => (
                                    <div key={comediante.comedianteId} className="bg-zinc-700 rounded-lg p-6 hover:bg-zinc-600 transition-colors duration-200">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-white mb-2">
                                                    {comediante.nombreComediante}
                                                </h3>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                    <div className="flex items-center text-gray-300">
                                                        <FaEye className="w-4 h-4 mr-2 text-blue-400" />
                                                        <span>{formatNumber(comediante.totalVistas)} vistas</span>
                                                    </div>
                                                    <div className="flex items-center text-gray-300">
                                                        <FaFileAlt className="w-4 h-4 mr-2 text-green-400" />
                                                        <span>{comediante.totalVideos} videos</span>
                                                    </div>
                                                    <div className="flex items-center text-gray-300">
                                                        <FaTicketAlt className="w-4 h-4 mr-2 text-purple-400" />
                                                        <span>{formatNumber(comediante.totalTicketsVendidos)} tickets</span>
                                                    </div>
                                                    <div className="flex items-center text-gray-300">
                                                        <FaDollarSign className="w-4 h-4 mr-2 text-yellow-400" />
                                                        <span>{formatCurrency(comediante.ingresosTotales)}</span>
                                                   </div>
                                               </div>
                                           </div>
                                       </div>

                                       {/* Botones de descarga */}
                                       <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                           {/* Informe Completo */}
                                           <button
                                               onClick={() => handleDescargarInforme(comediante.comedianteId, comediante.nombreComediante)}
                                               disabled={downloadingStates[comediante.comedianteId]}
                                               className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-3 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                           >
                                               {downloadingStates[comediante.comedianteId] ? (
                                                   <>
                                                       <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                                       Generando...
                                                   </>
                                               ) : (
                                                   <>
                                                       <FaDownload className="w-4 h-4 mr-2" />
                                                       Completo
                                                   </>
                                               )}
                                           </button>

                                           {/* Informe Solo Videos */}
                                           <button
                                               onClick={() => handleDescargarInformeVideos(comediante.comedianteId, comediante.nombreComediante)}
                                               disabled={downloadingStates[`videos_${comediante.comedianteId}`]}
                                               className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-4 py-3 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                           >
                                               {downloadingStates[`videos_${comediante.comedianteId}`] ? (
                                                   <>
                                                       <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                                       Generando...
                                                   </>
                                               ) : (
                                                   <>
                                                       <FaVideo className="w-4 h-4 mr-2" />
                                                       Videos
                                                   </>
                                               )}
                                           </button>

                                           {/* Informe Solo Eventos */}
                                           <button
                                               onClick={() => handleDescargarInformeEventos(comediante.comedianteId, comediante.nombreComediante)}
                                               disabled={downloadingStates[`eventos_${comediante.comedianteId}`]}
                                               className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-4 py-3 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                           >
                                               {downloadingStates[`eventos_${comediante.comedianteId}`] ? (
                                                   <>
                                                       <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                                       Generando...
                                                   </>
                                               ) : (
                                                   <>
                                                       <FaUsers className="w-4 h-4 mr-2" />
                                                       Eventos
                                                   </>
                                               )}
                                           </button>

                                           {/* Indicador de performance */}
                                           <div className="bg-zinc-600 px-4 py-3 rounded-lg flex items-center justify-center">
                                               <div className="text-center">
                                                   <div className="text-xs text-gray-400 mb-1">Performance</div>
                                                   <div className="flex items-center">
                                                       {comediante.totalVistas > 50000 && comediante.ingresosTotales > 1000000 ? (
                                                           <span className="text-green-400 text-sm font-semibold">⭐ Alto</span>
                                                       ) : comediante.totalVistas > 10000 || comediante.ingresosTotales > 500000 ? (
                                                           <span className="text-yellow-400 text-sm font-semibold">📈 Medio</span>
                                                       ) : (
                                                           <span className="text-gray-400 text-sm font-semibold">📊 Básico</span>
                                                       )}
                                                   </div>
                                               </div>
                                           </div>
                                       </div>
                                   </div>
                               ))}
                           </div>
                       )}
                   </div>
               )}

               {/* Footer con información adicional */}
               <div className="mt-8 bg-zinc-800 rounded-xl shadow-lg p-6">
                   <h3 className="text-lg font-semibold text-white mb-4">Información sobre los Reportes</h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                       <div className="bg-zinc-700 rounded-lg p-4">
                           <div className="flex items-center mb-2">
                               <FaFileAlt className="text-blue-400 mr-2" />
                               <span className="font-medium text-white">Completo</span>
                           </div>
                           <p className="text-gray-400">Incluye videos y eventos con análisis detallado y gráficos comparativos.</p>
                       </div>
                       <div className="bg-zinc-700 rounded-lg p-4">
                           <div className="flex items-center mb-2">
                               <FaVideo className="text-green-400 mr-2" />
                               <span className="font-medium text-white">Videos</span>
                           </div>
                           <p className="text-gray-400">Análisis exclusivo de videos: vistas, calificaciones, tags y performance.</p>
                       </div>
                       <div className="bg-zinc-700 rounded-lg p-4">
                           <div className="flex items-center mb-2">
                               <FaUsers className="text-purple-400 mr-2" />
                               <span className="font-medium text-white">Eventos</span>
                           </div>
                           <p className="text-gray-400">Métricas de eventos: tickets vendidos, ingresos, ocupación por sección.</p>
                       </div>
                       <div className="bg-zinc-700 rounded-lg p-4">
                           <div className="flex items-center mb-2">
                               <FaChartBar className="text-orange-400 mr-2" />
                               <span className="font-medium text-white">Comparativo</span>
                           </div>
                           <p className="text-gray-400">Ranking y comparación entre todos los comediantes de la plataforma.</p>
                       </div>
                   </div>
               </div>
           </main>
       </div>
   );
};

export default Reportes;